import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { HiCalendar } from 'react-icons/hi'
import { FaTrophy } from 'react-icons/fa'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair } from '../../utils/seoUtils'
import './ChampionsLeague.css'

function ChampionsLeague() {
  const { t, i18n } = useTranslation()
  const isJapanese = i18n.language === 'ja'
  const isRussian = i18n.language === 'ru'
  
  const getDisciplineName = (id) => {
    const names = {
      kubo: { ru: 'КУБО', en: 'KUBO', ja: 'КУБО' },
      aikido: { ru: 'Айкидо', en: 'Aikido', ja: '合気道' },
      aikijutsu: { ru: 'Айки-дзюцу', en: 'Aiki-jutsu', ja: '合気術' },
      jujutsu: { ru: 'Дзю-дзюцу', en: 'Jujutsu', ja: '柔術' },
      aikibujutsu: { ru: 'Айки-бу-дзюцу', en: 'Aiki-bu-jutsu', ja: '合気武術' },
      karatejutsu: { ru: 'Карате-дзюцу', en: 'Karate-jutsu', ja: '空手術' },
      kenjutsu: { ru: 'Кэн-дзюцу', en: 'Kenjutsu', ja: '剣術' },
      kempo: { ru: 'Кемпо', en: 'Kempo', ja: '拳法' }
    }
    const lang = isJapanese ? 'ja' : (isRussian ? 'ru' : 'en')
    return names[id] ? names[id][lang] : id
  }
  const getLocalizedLink = useLocalizedLink()

  const breadcrumbItems = useMemo(
    () => seoBreadcrumbPair(t('common.home'), t('common.championsLeague'), 'champions-league'),
    [t]
  )

  // Mock data
  const champions = []

  return (
    <div className="champions-league-page">
      <SEO 
        title={t('common.championsLeague')}
        description={t('championsLeague.description', 'Champions League of SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="champions-league-hero">
          <FaTrophy className="hero-icon" />
          <h1>{t('common.championsLeague')}</h1>
          <p className="champions-league-intro">
            {t('championsLeague.intro', 'The Champions League recognizes the top competitors and champions in SUMA International competitions.')}
          </p>
        </div>

        <section className="champions-section">
          <h2>{t('championsLeague.currentChampions', 'Current Champions')}</h2>
          <div className="champions-grid">
            {champions.length > 0 ? (
              champions.map(champion => (
                <div key={champion.id} className="champion-card">
                  <div className="champion-photo-placeholder">
                    <FaTrophy />
                  </div>
                  <h3>{champion.name}</h3>
                  <p className="champion-discipline">{champion.discipline ? getDisciplineName(champion.discipline) : ''}</p>
                  <p className="champion-category">{champion.category}</p>
                  <div className="champion-date">
                    <HiCalendar />
                    <span>{champion.date}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-champions">
                <p>{t('championsLeague.noChampions', 'Champions will be displayed here as competitions are held and results are recorded.')}</p>
              </div>
            )}
          </div>
        </section>

        <section className="champions-section">
          <h2>{t('championsLeague.competitions', 'Competitions')}</h2>
          <p>{t('championsLeague.competitionsText', 'Champions League competitions are held regularly, bringing together the best practitioners from member schools and federations.')}</p>
          <div className="champions-actions">
            <Link to={getLocalizedLink('/events')} className="btn btn-primary">
              {t('common.events')}
            </Link>
            <Link to={getLocalizedLink('/competition-rules')} className="btn btn-secondary">
              {t('common.competitionRules')}
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}

export default ChampionsLeague
